import { useState, useRef, MouseEvent } from 'react';

interface Especialista {
  nome: string;
  titulo: string;
  empresa: string;
  bio: string;
  foto: string;
  fotoPosition: string;
  tags: string[];
  icon: string;
  accentColor: string;
  accentBg: string;
  tagBg: string;
  igPessoal: string;
  igEmpresa: string;
  emBreve?: boolean;
}

const especialistas: Especialista[] = [
  {
    nome: 'Jonatha Oliveira',
    titulo: 'Gestão & Financeiro',
    empresa: 'Faço a Conta · Econoserv Consultoria',
    bio: 'Empresário e CEO com mais de 10 anos de experiência transformando negócios. Especialista em gestão empresarial, organização financeira e estruturação de processos. Já ajudou centenas de empreendedores a saírem do caos operacional e alcançarem resultados financeiros consistentes.',
    foto: 'https://static.readdy.ai/image/5da477fdae7b1d111ee386eccbf37db2/5b20c362f1d923b08a38d41763420414.png',
    fotoPosition: 'object-top',
    tags: ['Gestão', 'Financeiro', 'Planejamento'],
    icon: 'ri-trophy-line',
    accentColor: 'from-amber-400 to-orange-500',
    accentBg: 'bg-amber-500',
    tagBg: 'bg-amber-500/15 text-amber-700',
    igPessoal: 'https://instagram.com/jonathaoliveiraam',
    igEmpresa: 'https://instagram.com/facoaconta',
  },
  {
    nome: 'Léo David Cunha',
    titulo: 'Inovação, Tech & IA',
    empresa: 'X Lab Studio — Hub de Inovação',
    bio: 'Empresário, Founder e Diretor no X Lab Studio. Especialista em tecnologia e inovação com +12 anos de experiência. Mentor e facilitador de automação de processos e aplicação de IA na prática para negócios. Lidera projetos de transformação digital que aumentam a produtividade e competitividade das empresas.',
    foto: 'https://static.readdy.ai/image/5da477fdae7b1d111ee386eccbf37db2/c0ab1fe3b0b29c3f3460fc6419008329.png',
    fotoPosition: 'object-top',
    tags: ['Inovação', 'Automação', 'IA'],
    icon: 'ri-robot-2-line',
    accentColor: 'from-sky-400 to-cyan-500',
    accentBg: 'bg-sky-500',
    tagBg: 'bg-sky-500/15 text-sky-700',
    igPessoal: 'https://instagram.com/leodavidxp',
    igEmpresa: 'https://instagram.com/xlab.studio',
  },
  {
    nome: 'Joelton Oliveira',
    titulo: 'Comercial & Vendas',
    empresa: 'Faço a Conta · Econoserv Consultoria',
    bio: 'Sócio Econoserv · Especialista nas áreas: Comercial, Vendas e Negociação. Apaixonado por ajudar empresas a construírem equipes de alto desempenho e processos comerciais que geram resultados previsíveis e sustentáveis.',
    foto: '/joelton.png',
    fotoPosition: 'object-top',
    tags: ['Comercial', 'Vendas', 'Performance'],
    icon: 'ri-hand-coin-line',
    accentColor: 'from-rose-500 to-red-600',
    accentBg: 'bg-rose-500',
    tagBg: 'bg-rose-500/15 text-rose-700',
    igPessoal: '',
    igEmpresa: 'https://www.econoserv.com.br/',
  },
  {
    nome: 'Bônus Exclusivo',
    titulo: 'Assessoria Jurídica',
    empresa: 'Direito Empresarial Gratuito',
    bio: 'Como bônus do programa, você terá acesso a consultas jurídicas especializadas em Direito do Trabalho e Empresarial para proteger e fortalecer seu negócio desde o primeiro dia.',
    foto: '/bonus-card.png',
    fotoPosition: 'object-center',
    tags: ['Bônus', 'Jurídico', 'Consultoria'],
    icon: 'ri-shield-check-line',
    accentColor: 'from-emerald-500 to-teal-600',
    accentBg: 'bg-emerald-500',
    tagBg: 'bg-emerald-500/15 text-emerald-700',
    igPessoal: '',
    igEmpresa: '',
  },
];

interface CardProps {
  esp: Especialista;
}

function EspecialistaCard({ esp }: CardProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  const [glowPos, setGlowPos] = useState({ x: 50, y: 50 });
  const [hovered, setHovered] = useState(false);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });

  const handleMouseMove = (e: MouseEvent<HTMLDivElement>) => {
    const card = cardRef.current;
    if (!card) return;
    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const cx = rect.width / 2;
    const cy = rect.height / 2;
    // Tilt suave — máximo 4 graus
    setTilt({
      x: ((y - cy) / cy) * -4,
      y: ((x - cx) / cx) * 4,
    });
    setGlowPos({ x: (x / rect.width) * 100, y: (y / rect.height) * 100 });
  };

  const handleMouseLeave = () => {
    setTilt({ x: 0, y: 0 });
    setHovered(false);
  };

  const handleMouseEnter = () => setHovered(true);

  return (
    <div
      ref={cardRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onMouseEnter={handleMouseEnter}
      style={{
        transform: `perspective(1200px) rotateX(${tilt.x}deg) rotateY(${tilt.y}deg) ${hovered ? 'scale(1.02)' : 'scale(1)'}`,
        transition: hovered ? 'transform 0.15s ease-out' : 'transform 0.4s ease-out',
        willChange: 'transform',
      }}
      className="relative bg-white rounded-3xl overflow-hidden border border-stone-100 flex flex-col cursor-default"
    >
      {/* Glow sutil no hover */}
      <div
        className="absolute inset-0 pointer-events-none z-10 rounded-3xl transition-opacity duration-300"
        style={{
          opacity: hovered ? 1 : 0,
          background: `radial-gradient(circle at ${glowPos.x}% ${glowPos.y}%, rgba(251,191,36,0.07) 0%, transparent 60%)`,
        }}
      />

      {/* ── FOTO ── */}
      {esp.foto ? (
        <div className="relative w-full overflow-hidden" style={{ aspectRatio: '3/4' }}>
          <img
            src={esp.foto}
            alt={`Foto de ${esp.nome}`}
            className={`w-full h-full object-cover ${esp.fotoPosition} transition-transform duration-500 ${hovered ? 'scale-105' : 'scale-100'}`}
          />

          {/* Gradiente inferior */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />

          {/* Nome + cargo + badge + sociais na base da foto */}
          <div className="absolute bottom-0 left-0 right-0 p-5 z-10">
            <h3 className="text-xl font-black text-white leading-tight mb-0.5">{esp.nome}</h3>
            <p className="text-white/60 text-xs font-medium mb-3">{esp.empresa}</p>

            {/* Badge especialidade + redes sociais */}
            <div className="flex flex-wrap items-center gap-2">
              {/* Badge */}
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/40 backdrop-blur-sm border border-white/15 min-w-0 overflow-hidden">
                <i className={`${esp.icon} text-white text-xs flex-shrink-0`} />
                <span className="text-white/90 text-xs font-semibold truncate">{esp.titulo}</span>
              </div>

              {/* Botões Instagram */}
              <div className="flex items-center gap-1.5 flex-shrink-0 ml-auto">
                {esp.igPessoal && (
                  <a
                    href={esp.igPessoal}
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="w-7 h-7 flex items-center justify-center bg-black/40 backdrop-blur-sm hover:bg-white/25 border border-white/20 hover:border-white/50 rounded-full transition-all cursor-pointer"
                    title={esp.igPessoal.replace('https://instagram.com/', '@')}
                  >
                    <i className="ri-instagram-line text-white text-xs" />
                  </a>
                )}
                {esp.igEmpresa && (
                  <a
                    href={esp.igEmpresa}
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="w-7 h-7 flex items-center justify-center bg-black/40 backdrop-blur-sm hover:bg-white/25 border border-white/20 hover:border-white/50 rounded-full transition-all cursor-pointer"
                    title={esp.igEmpresa.replace('https://instagram.com/', '@')}
                  >
                    <i className="ri-building-line text-white text-xs" />
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* Sem foto: mostrar badge no topo com mesma altura da foto */
        <div className="relative w-full bg-gradient-to-br from-emerald-500 to-teal-600 flex flex-col justify-end" style={{ aspectRatio: '3/4' }}>
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
          <div className="relative z-10 p-5">
            <div className="flex items-center gap-2 mb-2">
              <i className={`${esp.icon} text-white text-xl`} />
              <span className="text-white text-xs font-semibold truncate">{esp.titulo}</span>
            </div>
            <h3 className="text-xl font-black text-white leading-tight mb-0.5">{esp.nome}</h3>
            <p className="text-white/70 text-xs font-medium">{esp.empresa}</p>
          </div>
        </div>
      )}

      {/* ── CONTEÚDO ABAIXO ── */}
      <div className="p-5 flex flex-col flex-1">
        {/* Linha decorativa */}
        <div className={`h-0.5 w-10 rounded-full bg-gradient-to-r ${esp.accentColor} mb-4`} />

        {/* Bio */}
        <p
          className="text-gray-500 text-sm leading-relaxed flex-1 mb-4"
          dangerouslySetInnerHTML={{ __html: esp.bio }}
        />

        {/* Tags */}
        <div className="flex flex-wrap gap-1.5">
          {esp.tags.map((tag) => (
            <span key={tag} className={`px-2.5 py-1 rounded-full text-xs font-semibold ${esp.tagBg}`}>
              {tag}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function EspecialistasSection() {
  return (
    <section id="especialistas" className="py-24 md:py-32 bg-stone-50">
      <div className="max-w-7xl mx-auto px-6 md:px-12">

        {/* Header */}
        <div className="text-center mb-16">
          <p className="text-xs font-semibold tracking-[0.25em] uppercase text-stone-400 mb-4">
            Quem vai te guiar
          </p>
          <h2 className="text-4xl md:text-5xl font-black text-gray-900 leading-tight mb-4">
            CONHEÇA OS<br />
            <span className="text-amber-500">ESPECIALISTAS</span>
          </h2>
          <p className="text-gray-500 text-lg max-w-xl mx-auto">
            Profissionais com trajetória comprovada, prontos para transformar o seu negócio na prática.
          </p>
        </div>

        {/* Grid 4 colunas */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 items-stretch">
          {especialistas.map((esp) => (
            <div key={esp.nome} className="flex flex-col h-full">
              <EspecialistaCard esp={esp} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
